/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, Link, Stack, useTheme } from "@mui/material";
import { usePresentChemistryGroups } from "./dependencies";
import { useHandleTableMutations } from "./hooks";
import React, { useMemo } from "react";
import {
  SearchPeriodProductionPlan,
  TargetBasketItem,
} from "src/store/api/generatedApi";
import {
  SearchChemistryGroups,
  SearchProductGroups,
  SearchSteelGrades,
  SteelGrade,
} from "contexts/search/context";
import { SteelGradeChip } from "components/common/chips/steelGrade";
import { OverflowButton } from "components/common/buttons/overflow";
import { productionPlanToSteelGradeIds } from "src/utils/productionPlanToSteelGradeIds";
import { MixPropertiesPanel } from "components/common/panels/mixDetails/MixPropertiesPanel";
import { GroupedProductionBasketsGrid } from "components/targetBaskets/GroupedProductionBasketsGrid";

export interface TargetBasketRow {
  id: number;
  groupId: number;
  groupName: string;
  productGroupId: number;
  productGroupName: string;
  target: number;
  steelGrades: SteelGrade[];
}

export interface Props {
  productionPlans: SearchPeriodProductionPlan[];
  productGroups: SearchProductGroups;
  chemistryGroups: SearchChemistryGroups;
  targetBaskets: TargetBasketItem[];
  maxBaskets: number;
  steelGrades: SearchSteelGrades;
}

export const TargetBasketsTable = ({
  productionPlans,
  chemistryGroups,
  productGroups,
  targetBaskets,
  maxBaskets,
  steelGrades,
}: Props) => {
  const theme = useTheme();
  const { handleUpdateItem } = useHandleTableMutations(
    maxBaskets,
    chemistryGroups
  );

  const steelGradesVisible = productionPlans.some(
    (productionPlan) => productionPlan.steel_grade_items !== null
  );
  const presentSteelGrades = useMemo(
    () =>
      new Set(
        productionPlans.flatMap((productionPlan) =>
          productionPlanToSteelGradeIds(productionPlan, productGroups)
        )
      ),
    [productionPlans, productGroups]
  );

  const rows: TargetBasketRow[] = usePresentChemistryGroups(
    productionPlans,
    productGroups,
    chemistryGroups
  ).map((id) => {
    const chemistryGroup = chemistryGroups.byId[id]!;
    const productGroup = productGroups.byId[chemistryGroup.product_group_id];
    const item =
      targetBaskets.find((item) => item.chef_group_id === id) ?? null;
    return {
      id: chemistryGroup.id,
      steelGrades: Array.from(presentSteelGrades)
        .filter((steel_grade_id) =>
          chemistryGroup.steel_grades
            .map(({ id }) => id)
            .includes(steel_grade_id)
        )
        .map((steel_grade_id) => steelGrades.byId[steel_grade_id]!),
      groupId: chemistryGroup.id,
      groupName: chemistryGroup.name,
      productGroupId: chemistryGroup.product_group_id,
      productGroupName: productGroup?.name ?? "",
      target:
        item?.target_num_baskets ?? chemistryGroup.default_target_num_baskets,
    };
  });

  return (
    <Stack
      sx={{ height: "100%" }}
      onKeyDown={(e) => {
        if (e.key === "Escape") e.stopPropagation();
      }}
    >
      <GroupedProductionBasketsGrid
        rows={rows}
        processRowUpdate={handleUpdateItem}
        renderGroupCell={(params) =>
          params.row.groupId ? (
            <ChemistryGroup
              id={params.row.groupId}
              name={params.row.groupName}
            />
          ) : null
        }
        renderSteelGradesCell={
          steelGradesVisible
            ? (params) =>
                params.row.steelGrades ? (
                  <SteelGradesCell steelGrades={params.row.steelGrades} />
                ) : null
            : undefined
        }
        columnVisibilityModel={
          steelGradesVisible ? undefined : { steelGrades: false }
        }
        sx={[
          {
            "& .MuiDataGrid-cell--editing input": {
              paddingX: 1,
              ...theme.typography.body1Mono,
            },
          },
        ]}
        cellSelection
        ignoreValueFormatterDuringExport
      />
    </Stack>
  );
};

interface ChemistryGroupProps {
  id: number;
  name: string;
}

const ChemistryGroup = ({ id, name }: ChemistryGroupProps) => {
  const theme = useTheme();
  const [showModal, setShowModal] = React.useState(false);

  return (
    <>
      <Link
        variant="body2"
        color={theme.palette.text.primary}
        fontWeight="bold"
        sx={{ cursor: "pointer" }}
        onClick={() => {
          setShowModal(true);
        }}
      >
        {name}
      </Link>
      <MixPropertiesPanel group={id} open={showModal} setOpen={setShowModal} />
    </>
  );
};

const SteelGradesCell = ({ steelGrades }: { steelGrades: SteelGrade[] }) => {
  const maxChipsBeforeOverflow = 6;
  const gridSteelGrades = steelGrades.slice(0, maxChipsBeforeOverflow);
  const overflowSteelGrades = steelGrades.slice(maxChipsBeforeOverflow);
  return (
    <Stack
      flexDirection="row"
      alignItems="center"
      gap={0.5}
      sx={{ paddingY: 1 }}
    >
      {gridSteelGrades.map((steelGrade) => (
        <SteelGradeChip
          key={steelGrade.id}
          id={steelGrade.id}
          name={steelGrade.name}
          chemicalConstraints={steelGrade.chemical_constraints}
        />
      ))}
      {overflowSteelGrades.length > 0 ? (
        <OverflowButton>
          <Box flexWrap="wrap" sx={{ p: 1, maxWidth: 300 }}>
            {overflowSteelGrades.map((steelGrade) => (
              <SteelGradeChip
                key={steelGrade.id}
                id={steelGrade.id}
                name={steelGrade.name}
                chemicalConstraints={steelGrade.chemical_constraints}
              />
            ))}
          </Box>
        </OverflowButton>
      ) : null}
    </Stack>
  );
};
